import React from "react";
import { useTranslation } from "react-i18next";
import BaseMaterialButton from "@reusables/BaseMaterialButton";
import { Can } from "@/casl.config";
import { StockCount } from "@/types/general";

import { ReactComponent as ApproveSVG } from "@assets/icons/ic_approve_stock.svg";
import { ReactComponent as DeclineSVG } from "@assets/icons/ic_decline_stock.svg";

import StockCountEntriesTable from "./StockCountEntriesTable";
import MergeOriginsTree from "./MergeOriginsTree";

interface StockCountCollapseProperties {
    count: StockCount.Root;
    onStatusChangeRequest: (count: StockCount.Root, action: "approve" | "decline") => void;
}

/**
 * Body of an expanded stock count row.
 *
 * Extracted into a real component because `BaseTable`'s `collapse.content` is a render callback
 * invoked mid-render of the parent, so the pagination/search hooks below cannot live there.
 */
export default function StockCountCollapse({
    count,
    onStatusChangeRequest,
}: StockCountCollapseProperties) {
    const { t } = useTranslation("", { keyPrefix: "inventory.stockCount.main" });

    return (
        <div className="space-y-[16px] p-[8px]">
            <StockCountEntriesTable entries={count.entries} />

            {count.is_merge && <MergeOriginsTree origins={count.included_in_merge} />}

            <div className="space-y-2 ml-4">
                {count.cancel_comment && (
                    <div>
                        <div className="flex">
                            <span className="text-accent font-semibold mr-2">
                                {count.user?.first_name + " " + count.user?.last_name}
                            </span>
                        </div>
                        <div>{count.cancel_comment}</div>
                    </div>
                )}

                {count.decline_comment && (
                    <div>
                        <div className="flex">
                            <span className="text-accent font-semibold mr-2">
                                {count.reviewer?.first_name + " " + count.reviewer?.last_name}
                            </span>
                        </div>
                        <div>{count.decline_comment}</div>
                    </div>
                )}

                {count.status === 0 && (
                    <div className="flex gap-4">
                        <Can I="create" a="stock_count">
                            <BaseMaterialButton
                                onClick={() => onStatusChangeRequest(count, "approve")}
                            >
                                <ApproveSVG className="text-positive mr-2" />
                                <span className="text-positive">
                                    {t("subtable.buttons.approve")}
                                </span>
                            </BaseMaterialButton>
                        </Can>
                        <Can I="create" a="stock_count">
                            <BaseMaterialButton
                                onClick={() => onStatusChangeRequest(count, "decline")}
                            >
                                <DeclineSVG className="text-error mr-2" />
                                <span className="text-error">{t("subtable.buttons.decline")}</span>
                            </BaseMaterialButton>
                        </Can>
                    </div>
                )}
            </div>
        </div>
    );
}
